import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FolderKanban,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Truck,
  ClipboardList,
  ChevronRight,
  ShieldCheck,
  Layers,
  Clock,
  Boxes,
  UserX,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import KPITile from "../components/KPITile";
import StatusBadge from "../components/StatusBadge";
import { contentKey, counted, staggered, useIntro } from "../components/intro";
import ConfirmDialog from "../StandardsLibrary/ConfirmDialog";
import AddProjectModal from "./AddProjectModal";
import ProjectWorkspace from "./ProjectWorkspace";
import AuditTrail from "./AuditTrail";
import AwpWorkspace from "../Awp/AwpWorkspace";
import { createProject, deleteProject, listProjects, updateProject } from "./api";
import {
  EMPTY_STATS,
  ProjectDraft,
  STATUS_LABELS,
  STATUS_TONE,
  VerityProject,
  deriveProjectProgress,
  reviewersOf,
  reviewTypeOf,
} from "./types";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

/**
 * The project list.
 *
 * A project is the unit everything else in the module hangs off: deliverables are submitted against
 * one, and findings are raised against those.
 *
 * TWO AUDIENCES, the SMRT COP shape. An ADMINISTRATOR creates a project, assigns a reviewer to it
 * and follows what they do — this is their dashboard, and the counts on top are the whole point of
 * it. A REVIEWER sees only the projects and opens the one they are working; creating, editing and
 * removing are not theirs.
 */
interface ProjectsProps {
  /**
   * Rendered from the admin shell.
   *
   * Gates everything that is the administrator's rather than the reviewer's: creating, editing and
   * removing projects, and the audit trail. It also travels into the workspace, where it makes the
   * stages read-only — an administrator follows a project, they do not do its work.
   */
  admin?: boolean;
  /**
   * Which side of the mode switch is showing. The list holds only that side's projects, and an AWP
   * project opens into its package register rather than the deliverables pipeline.
   */
  reviewType?: "engineering" | "awp";
}

const Projects = ({ admin = false, reviewType = "engineering" }: ProjectsProps) => {
  const awp = reviewType === "awp";
  const [allProjects, setProjects] = useState<VerityProject[] | null>(null);
  // Filtered here rather than on the server: an org has tens of projects, and one list read serves
  // both sides of the switch.
  const projects = useMemo(
    () => allProjects?.filter((p) => reviewTypeOf(p) === reviewType) ?? null,
    [allProjects, reviewType],
  );
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<VerityProject | null>(null);
  const [confirming, setConfirming] = useState<VerityProject | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * The project being worked in. The list stays a list; this is the workbench.
   *
   * IN THE URL, NOT IN STATE. Two reasons, and the second is why it moved. A refresh inside a
   * project used to drop you back on the list — the same complaint that put the sidebar tab in the
   * URL. And because the sidebar could not see this, pressing "Admin Console" while inside a
   * project did nothing at all: the tab was already `projects`, so there was no change to react to.
   * As a parameter it is something the sidebar can clear — see NESTED_PARAMS in useNavTab.
   */
  const [params, setParams] = useSearchParams();
  const openId = params.get("project");
  const setOpenId = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(params);
      if (id) next.set("project", id);
      else next.delete("project");
      // Replace, for the same reason the tab does: moving within a screen should not make Back
      // retrace every project you glanced at.
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  /** The "what this is" note. Closed by default — it is an explanation, not a heading. */
  const [helpOpen, setHelpOpen] = useState(false);
  /** The project whose audit trail is open, over the list. Administrators only. */
  const [auditFor, setAuditFor] = useState<VerityProject | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      // The reviewer screen asks for its own work explicitly, rather than leaving the server to
      // infer which shell asked. An administrator holds the permission to see everything, so
      // without this they saw every project here too — the admin dashboard on the wrong screen.
      setProjects(await listProjects(admin ? undefined : "mine"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load projects.");
      setProjects([]);
    }
  }, [admin]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    const rows = projects ?? [];
    const statusOf = (p: VerityProject) =>
      p.status ?? deriveProjectProgress(p.stats ?? EMPTY_STATS, p.complianceRunAt).status;
    return {
      total: rows.length,
      awaiting: rows.filter((p) => statusOf(p) === "submitted").length,
      ready: rows.filter((p) => statusOf(p) === "ready").length,
      suppliers: new Set(rows.map((p) => p.supplier).filter(Boolean)).size,
      packages: rows.reduce((sum, p) => sum + (p.packageCount ?? 0), 0),
      unassigned: rows.filter((p) => reviewersOf(p).length === 0).length,
    };
  }, [projects]);

  // Keyed on what the rows SAY rather than on the array holding them: adding or deleting a project
  // replays the count, while a re-fetch returning the same data leaves it alone. See contentKey.
  const introKey = useMemo(
    () =>
      contentKey(
        projects,
        (p) => `${p.id}:${p.updatedAt ?? ""}:${p.complianceRunAt ?? ""}:${p.stats?.parsed ?? 0}`,
      ),
    [projects],
  );
  const t = useIntro(introKey);

  const add = async (draft: ProjectDraft) => {
    const created = await createProject(draft);
    setAddOpen(false);
    setProjects((prev) => [created, ...(prev ?? [])]);
  };

  const saveEdit = async (draft: ProjectDraft) => {
    if (!editing) return;
    const updated = await updateProject(editing.id, draft);
    setEditing(null);
    setProjects((prev) => (prev ?? []).map((p) => (p.id === updated.id ? updated : p)));
  };

  const remove = async (project: VerityProject) => {
    setBusyId(project.id);
    setError(null);
    try {
      await deleteProject(project.id);
      setProjects((prev) => (prev ?? []).filter((p) => p.id !== project.id));
      if (openId === project.id) setOpenId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove the project.");
    } finally {
      setBusyId(null);
    }
  };

  const open = (projects ?? []).find((p) => p.id === openId);
  if (open) {
    return awp ? (
      <AwpWorkspace project={open} admin={admin} onBack={() => setOpenId(null)} />
    ) : (
      <ProjectWorkspace project={open} admin={admin} onBack={() => setOpenId(null)} />
    );
  }

  return (
    // The frame — title, Add Project, the counts — is permanent furniture: it answers "where am I
    // and how many are there", and losing it on the way down a long list is what makes a workspace
    // read as a document. Only the list under it moves. Same pattern as the Requirements workbench.
    <div className="vr-page vr-page-fixed">
      <div className="vr-page-head">
        <div>
          {/* Worth reading once. Left permanently under the heading it pushes the counts and the
              list itself down the screen on every visit. Same "i" toggle Findings uses. */}
          <h1 className="vr-h-page">
            {admin ? "Admin Console" : "Projects"}
            <button
              type="button"
              className="vr-info-btn"
              aria-expanded={helpOpen}
              title={helpOpen ? "Hide" : "What this is"}
              onClick={() => setHelpOpen((open) => !open)}
            >
              i
            </button>
          </h1>
          <p hidden={!helpOpen}>
            {awp
              ? admin
                ? "Each AWP project holds the CWP, EWP and PWP work packages to be reviewed. Create one, assign the reviewers who will work it, and follow their progress from here."
                : "Each AWP project holds the CWP, EWP and PWP work packages to be reviewed. Open one to enter its packages and see how they link together."
              : admin
                ? "Each project is a piece of work a supplier submits deliverables against. Create one, assign the reviewers who will work it, and follow their progress from here."
                : "Each project is a piece of work a supplier submits deliverables against. Open one to ingest its documents and run them past the governing standards."}
          </p>
        </div>
        {/* Projects are set up by an administrator and worked by the reviewer assigned to
            them, so creating one is not offered on the user side. */}
        {admin && (
          <button className="vr-btn vr-btn-glow" onClick={() => setAddOpen(true)}>
            <Plus size={15} /> Add Project
          </button>
        )}
      </div>

      {/* Counted up on the shared clock, each tile a beat behind the one before it — the same
          entrance the Executive Dashboard uses, so the two screens arrive the same way. */}
      <div className="vr-grid vr-grid-4">
        {(awp
          ? [
              { icon: FolderKanban, label: "AWP Projects", value: counts.total },
              { icon: Boxes, label: "Work Packages", value: counts.packages },
              { icon: UserX, label: "Unassigned", value: counts.unassigned },
              { icon: Truck, label: "Contractors", value: counts.suppliers },
            ]
          : [
              { icon: FolderKanban, label: "Total Projects", value: counts.total },
              { icon: ClipboardList, label: "Awaiting Deliverables", value: counts.awaiting },
              { icon: ShieldCheck, label: "Ready for Compliance", value: counts.ready },
              { icon: Truck, label: "Suppliers", value: counts.suppliers },
            ]
        ).map((kpi, i) => (
          <KPITile
            key={kpi.label}
            icon={kpi.icon}
            label={kpi.label}
            value={counted(kpi.value, staggered(t, i))}
          />
        ))}
      </div>

      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-scroll-region">
        <GlowCard className="vr-table-card" hover={false}>
          <div className="vr-table-head">
            <h3>Projects ({projects?.length ?? 0})</h3>
            <button className="vr-rowbtn" onClick={() => void load()}>
              <RefreshCw size={12} /> Refresh
            </button>
          </div>

          <div className="vr-table-scroll">
            <table className="vr-table">
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Project ID</th>
                  <th>{awp ? "Contractor" : "Supplier"}</th>
                  <th>Reviewer</th>
                  <th>Date Added</th>
                  {/* Status and next step are derived from deliverables, which an AWP project does
                      not have. Its readiness will come from package reviews once they exist. */}
                  {awp ? (
                    <th>Packages</th>
                  ) : (
                    <>
                      <th>Status</th>
                      <th>Progress</th>
                    </>
                  )}
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(projects ?? []).map((project) => {
                  const busy = busyId === project.id;
                  // The server stamps these onto every row; recomputed here only so an older
                  // response, or an optimistically-added row, still renders something true.
                  const { status, progress } =
                    project.status && project.progress
                      ? { status: project.status, progress: project.progress }
                      : deriveProjectProgress(project.stats ?? EMPTY_STATS, project.complianceRunAt);
                  return (
                    // NOT click-anywhere any more. A whole row that navigates has no visible edge,
                    // so the Edit and Delete buttons inside it had to stop their own clicks from
                    // reaching it, and a stray click on a cell took you somewhere. Opening is an
                    // action, and it lives with the other actions.
                    <tr key={project.id}>
                      <td className="vr-cell-title">
                        <div>
                          <strong>{project.name}</strong>
                          {(project.location || project.discipline) && (
                            <div className="vr-cell-code">
                              {[project.location, project.discipline].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="vr-cell-dim">{project.projectRef || "—"}</td>
                      <td className="vr-cell-dim">{project.supplier}</td>
                      {/* Who is carrying it. An unassigned project is not an error — it is work
                          nobody has been given yet, which is worth seeing at a glance. */}
                      <td className={reviewersOf(project).length ? "vr-cell-dim" : "vr-warn"}>
                        {reviewersOf(project)
                          .map((r) => r.name)
                          .join(", ") || "Unassigned"}
                      </td>
                      <td className="vr-cell-dim">{formatDate(project.createdAt)}</td>
                      {awp ? (
                        <td className="vr-cell-dim">{project.packageCount ?? 0}</td>
                      ) : (
                        <>
                          <td>
                            <StatusBadge status={STATUS_TONE[status]} label={STATUS_LABELS[status]} />
                          </td>
                          {/* The next thing to do, not a restatement of the status. */}
                          <td>
                            <span className="vr-cell-next">
                              <ChevronRight size={12} />
                              {progress}
                            </span>
                          </td>
                        </>
                      )}
                      <td>
                        <div className="vr-cell-actions">
                          {/* The same mark the Standards Library uses to open a standard, doing the
                              same job here, so one icon means "open this" across the module. */}
                          <button
                            className="vr-rowbtn vr-icononly"
                            onClick={() => setOpenId(project.id)}
                            title="Open this project"
                            aria-label="Open project"
                          >
                            <Layers size={13} />
                          </button>
                          {/* THE RECORD, READ FROM THE LIST rather than from inside the project.
                              Who ran the check and who ruled on what is a question asked ABOUT a
                              review, usually by somebody answering for it — not a step in doing
                              one. It sat in the workspace header where it competed with the stage
                              you were actually on. */}
                          {admin && (
                            <button
                              className="vr-rowbtn vr-icononly"
                              onClick={() => setAuditFor(project)}
                              title="Audit trail — who did what, and when"
                              aria-label="Audit trail"
                            >
                              <Clock size={13} />
                            </button>
                          )}
                          {/* Editing and removing a project are the administrator's, not the
                              reviewer's — see the admin flag. */}
                          {admin && (
                          <button
                            className="vr-rowbtn vr-icononly"
                            onClick={() => setEditing(project)}
                            disabled={busy}
                            title="Edit this project"
                            aria-label="Edit project"
                          >
                            <Pencil size={13} />
                          </button>
                          )}
                          {admin && (
                          <button
                            className="vr-rowbtn vr-icononly vr-danger"
                            onClick={() => setConfirming(project)}
                            disabled={busy}
                            title="Remove this project"
                            aria-label="Remove project"
                          >
                            {busy ? <Loader2 size={13} className="vr-spin-inline" /> : <Trash2 size={13} />}
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {projects === null && (
            <div className="vr-empty">
              <Loader2 size={16} className="vr-spin-inline" /> Loading projects…
            </div>
          )}
          {projects !== null && projects.length === 0 && (
            // A reviewer's list is only what has been assigned to them, so empty means nobody has
            // given them anything — not that the org has no projects. Saying "add one" to somebody
            // with no Add button is the screen explaining itself to the wrong person.
            <div className="vr-empty">
              {admin
                ? awp
                  ? "No AWP projects yet. Add one to start entering work packages."
                  : "No projects yet. Add one to start ingesting deliverables."
                : awp
                  ? "No AWP projects assigned to you yet."
                  : "No projects assigned to you yet."}
            </div>
          )}
        </GlowCard>
      </div>

      {addOpen && (
        <AddProjectModal
          reviewType={reviewType}
          onCancel={() => setAddOpen(false)}
          onSubmit={add}
        />
      )}

      {editing && (
        <AddProjectModal
          reviewType={reviewType}
          project={editing}
          onCancel={() => setEditing(null)}
          onSubmit={saveEdit}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title="Remove this project?"
          tone="danger"
          confirmLabel="Remove permanently"
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const target = confirming;
            setConfirming(null);
            void remove(target);
          }}
        >
          <p className="vr-confirm-lead">
            <strong>{confirming.name}</strong>
            {confirming.projectRef ? ` — ${confirming.projectRef}` : ""}
          </p>
          {awp ? (
            <>
              <p>This deletes the project and every work package entered against it.</p>
              <p className="vr-confirm-stress">This cannot be undone.</p>
            </>
          ) : (
            <>
              <p>
                This deletes the project and every deliverable submitted against it, including the
                files themselves.
              </p>
              <p className="vr-confirm-stress">
                This cannot be undone. The supplier would have to submit their documents again.
              </p>
            </>
          )}
        </ConfirmDialog>
      )}

      {/* Over the list, not inside a project. Reading who did what is a question about a review
          rather than a step in one. */}
      {auditFor && (
        <AuditTrail
          project={auditFor}
          run={auditFor.complianceRun ?? null}
          onClose={() => setAuditFor(null)}
        />
      )}
    </div>
  );
};

export default Projects;
